/**
 * Composite data source enums, target kinds and workspace copy.
 *
 * Status values are the API's enum MEMBER NAMES, not ordinals: DataSourceDefinitionVM
 * projects Status as a string precisely so the contract survives the serializer
 * (DataSourceDefinitionVM.cs:23-28). Verified on the wire 2026-08-18 — the list answers
 * "Published", never 1.
 */

export const CompositeSourceStatus = Object.freeze({
  Draft: 'Draft',
  Published: 'Published',
  Broken: 'Broken',
});

export const CompositeSourceStatusOptions = [
  { text: 'Draft', value: CompositeSourceStatus.Draft, color: 'grey' },
  { text: 'Published', value: CompositeSourceStatus.Published, color: 'success' },
  { text: 'Broken', value: CompositeSourceStatus.Broken, color: 'error' },
];

export function getCompositeSourceStatusColor(value) {
  const found = CompositeSourceStatusOptions.find((o) => o.value === value);
  return found ? found.color : 'grey';
}

export function getCompositeSourceStatusText(value) {
  const found = CompositeSourceStatusOptions.find((o) => o.value === value);
  return found ? found.text : 'Unknown';
}

/**
 * Scope a target or a definition is bound to. `facility` is the only kind the M1 engine
 * compiles — the validator rejects anything else with UnknownScopeKind
 * (DataSourceDefinitionValidator.cs:290-297) — so group targets ship visible but disabled
 * until the group-scope engine update lands.
 */
export const CompositeSourceTargetKind = Object.freeze({
  Facility: 'facility',
  FacilityGroup: 'facilityGroup',
});

/**
 * Workspace copy. Kept here rather than inline so the wizard waves reuse the exact wording
 * the list already shows, and so the group-support message changes in ONE place when the
 * group-scope engine update lands.
 */
export const CompositeSourceCopy = Object.freeze({
  GroupTargetDisabledNote: 'Requires the group-scope engine update',
  GroupOnlyTargetsTitle: 'Group targets are not supported yet',
  GroupOnlyTargetsMessage:
    'Composite sources currently support facility targets — group support is coming with the group-scope engine update.',
  NoTargetsTitle: 'No targets available',
  NoTargetsMessage:
    'You do not have access to any facility yet. Ask an administrator to add you to a facility, then reopen this page.',
  NoTargetSelectedTitle: 'Pick a target to begin',
  NoTargetSelectedMessage:
    'Choose a facility above to see the composite sources built for it.',
  NoDefinitionsTitle: 'No composite sources yet',
  NoDefinitionsMessage:
    'Nothing has been built for this target. Create a composite source to join your starting data to the records around it.',
  RegistersFamilyHeader: 'Registers',
  TaskBoardsFamilyHeader: 'Task boards',
  TagsFamilyHeader: 'Tags',
  FindingsFamilyHeader: 'AI findings',
  FindingsRootLabel: 'All AI findings in this facility',
});
